import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import type { Options } from 'pusher-js';
import type { Channel } from 'laravel-echo';
import type { ZodTypeAny } from 'zod';

let pusherClient: InstanceType<typeof Pusher> | undefined;

export const getEcho = (uniqueClient = false) => {
    if (!import.meta.env.VITE_APP_PUSHER_CLUSTER || !import.meta.env.VITE_APP_PUSHER_KEY) {
        console.warn('Pusher not configured.');
        return undefined;
    }
    const options: Options & {
        broadcaster?: string;
        key: string;
    } = {
        broadcaster: 'pusher',
        key: import.meta.env.VITE_APP_PUSHER_KEY,
        cluster: import.meta.env.VITE_APP_PUSHER_CLUSTER,
        wsHost: import.meta.env.VITE_APP_ECHO_HOST,
        wsPort: Number.parseInt(import.meta.env.VITE_APP_ECHO_PORT ?? '6001'),
        wssPort: Number.parseInt(import.meta.env.VITE_APP_ECHO_PORT ?? '443'),
        httpHost: import.meta.env.VITE_APP_ECHO_HOST,
        httpPort: 80,
        forceTLS: false,
        disableStats: true,
        authTransport: 'ajax',
        channelAuthorization: {
            endpoint: import.meta.env.VITE_API_URL + '/broadcasting/auth',
            transport: 'ajax',
            headersProvider: async () => {
                const clientStore = useClientStore();
                if (!clientStore.auth.sessionValid) {
                    return {};
                }
                if (clientStore.isJwtExpired()) {
                    await clientStore.refreshJwtToken();
                }
                return { Authorization: 'Bearer ' + clientStore.auth.jwt };
            },
            customHandler: (params: any, callback: any) => {
                api.axios
                    .post<{ auth: string; channel_data?: string; shared_secret?: string }>('/broadcasting/auth', {
                        socket_id: params.socketId,
                        channel_name: params.channelName,
                    })
                    .then(response => callback(null, response.data))
                    .catch(error => callback(error, null));
            },
        },
        enabledTransports:
            import.meta.env.VITE_APP_ECHO_PORT === '443' ||
            import.meta.env.VITE_APP_ECHO_HOST?.includes('https') ||
            import.meta.env.VITE_APP_ECHO_HOST?.includes('wss')
                ? ['wss', 'ws']
                : ['ws'],
    };
    const pusher = uniqueClient
        ? new Pusher(options.key, options)
        : (pusherClient ??= new Pusher(options.key, options));
    return new Echo({
        broadcaster: options.broadcaster,
        key: options.key,
        client: pusher,
    });
};

export const validatedListen = <T extends ZodTypeAny>(
    channel: Channel,
    args: { readonly event: string; readonly schema: T },
    callback: (data: T['_output'] | undefined) => void | any,
) => {
    const recvChannel = channel.listen(args.event, async (data: T['_output']) => {
        const parsed = await args.schema.safeParseAsync(data);
        if (parsed.success) {
            console.info('Received event:', args.event, '\nValidated data:', parsed.data);
            callback(parsed.data);
        } else {
            console.info(
                'Unable to parse received event successfully. Will call handler without event data. \nEvent:',
                args.event,
                '\nData:',
                data,
                '\nErrors: ',
                parsed.error.errors,
            );
            callback(undefined);
        }
    });
    type Args = typeof args;
    type Callback = typeof callback;
    return {
        channel: recvChannel,
        validatedListen: (eventSchema: Args, callback: Callback) => validatedListen(recvChannel, eventSchema, callback),
    };
};
